// src/pages/trading-portal/MarketDataIngestion/StockPriceFeedPage.js
// Stock price feed management - tabbed interface

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Autocomplete,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    ShowChart as ShowChartIcon,
    Calculate as CalculateIcon,
    TrendingUp as TrendingUpIcon,
    Event as EventIcon,
    Assessment as AssessmentIcon,
    CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    PriceFeedStatus,
    PriceFeedStatusLabels,
    PriceFeedStatusColors,
    RVEstimatorType,
    RVEstimatorTypeLabels,
    CorporateActionType,
    CorporateActionTypeLabels,
    CorporateActionTypeColors,
    IVSourceLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const BROKER_API_LABELS = {
    yahoo_finance: 'Yahoo Finance',
    alpha_vantage: 'Alpha Vantage',
    polygon: 'Polygon.io',
    alpaca: 'Alpaca Markets',
};
const ACCENT = '#f59e0b';

const StockPriceFeedPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab
    const [activeTab, setActiveTab] = useState(0);

    // Symbols
    const [symbols, setSymbols] = useState([]);

    // Global state
    const [error, setError] = useState(null);

    // ========== Tab 0: Price Feed Configs ==========
    const [feeds, setFeeds] = useState([]);
    const [feedsLoading, setFeedsLoading] = useState(true);
    const [feedsPage, setFeedsPage] = useState(0);
    const [feedsRowsPerPage, setFeedsRowsPerPage] = useState(10);
    const [feedsTotalCount, setFeedsTotalCount] = useState(0);

    // Feed dialog
    const [feedDialogOpen, setFeedDialogOpen] = useState(false);
    const [feedEditMode, setFeedEditMode] = useState(false);
    const [selectedFeed, setSelectedFeed] = useState(null);
    const [feedFormData, setFeedFormData] = useState({
        symbol_id: null,
        broker_api: '',
        connection_type: 'websocket',
        refresh_interval_seconds: 60,
        status: 'STREAMING',
    });
    const [feedFormErrors, setFeedFormErrors] = useState({});
    const [feedSubmitting, setFeedSubmitting] = useState(false);

    // Feed delete
    const [feedDeleteDialogOpen, setFeedDeleteDialogOpen] = useState(false);
    const [feedToDelete, setFeedToDelete] = useState(null);

    // ========== Tab 1: Realized Volatility ==========
    const [rvSymbol, setRvSymbol] = useState(null);
    const [rvEstimator, setRvEstimator] = useState('');
    const [rvInterval, setRvInterval] = useState('DAILY');
    const [rvWindowDays, setRvWindowDays] = useState(20);
    const [rvRecords, setRvRecords] = useState([]);
    const [rvLoading, setRvLoading] = useState(false);
    const [rvCalculating, setRvCalculating] = useState(false);
    const [rvPage, setRvPage] = useState(0);
    const [rvRowsPerPage, setRvRowsPerPage] = useState(10);
    const [rvTotalCount, setRvTotalCount] = useState(0);

    // ========== Tab 2: Implied Volatility ==========
    const [ivSymbol, setIvSymbol] = useState(null);
    const [ivRecords, setIvRecords] = useState([]);
    const [ivLoading, setIvLoading] = useState(false);
    const [ivFetching, setIvFetching] = useState(false);
    const [ivPage, setIvPage] = useState(0);
    const [ivRowsPerPage, setIvRowsPerPage] = useState(10);
    const [ivTotalCount, setIvTotalCount] = useState(0);

    // ========== Tab 3: Corporate Actions ==========
    const [corpActions, setCorpActions] = useState([]);
    const [corpLoading, setCorpLoading] = useState(true);
    const [corpPage, setCorpPage] = useState(0);
    const [corpRowsPerPage, setCorpRowsPerPage] = useState(10);
    const [corpTotalCount, setCorpTotalCount] = useState(0);
    const [corpFilterSymbol, setCorpFilterSymbol] = useState('');
    const [corpFilterType, setCorpFilterType] = useState('');

    // Corp action dialog
    const [corpDialogOpen, setCorpDialogOpen] = useState(false);
    const [corpEditMode, setCorpEditMode] = useState(false);
    const [selectedCorpAction, setSelectedCorpAction] = useState(null);
    const [corpFormData, setCorpFormData] = useState({
        symbol_id: null,
        action_type: '',
        ex_date: '',
        record_date: '',
        pay_date: '',
        amount: '',
        ratio: '',
        description: '',
        affects_options: false,
    });
    const [corpFormErrors, setCorpFormErrors] = useState({});
    const [corpSubmitting, setCorpSubmitting] = useState(false);

    // Corp delete
    const [corpDeleteDialogOpen, setCorpDeleteDialogOpen] = useState(false);
    const [corpToDelete, setCorpToDelete] = useState(null);

    // ========== Tab 4: Earnings Calendar ==========
    const [earnings, setEarnings] = useState([]);
    const [earningsLoading, setEarningsLoading] = useState(true);
    const [earningsPage, setEarningsPage] = useState(0);
    const [earningsRowsPerPage, setEarningsRowsPerPage] = useState(10);
    const [earningsTotalCount, setEarningsTotalCount] = useState(0);
    const [earningsFilterSymbol, setEarningsFilterSymbol] = useState('');
    const [earningsUpcomingOnly, setEarningsUpcomingOnly] = useState(false);

    // ========== Permissions ==========
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-mdi-price-feed', 'view');
            const add = await hasPermission('tr-mdi-price-feed', 'add');
            const edit = await hasPermission('tr-mdi-price-feed', 'edit');
            const del = await hasPermission('tr-mdi-price-feed', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // ========== Fetch Symbols ==========
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchSymbols();
    }, [fetchSymbols, canView]);

    // ========== Tab 0: Fetch Price Feed Configs ==========
    const fetchFeeds = useCallback(async () => {
        if (!canView) return;
        setFeedsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({ page: feedsPage + 1, per_page: feedsRowsPerPage });
            const response = await axiosInstance.get(`/api/market-data-ingestion/price-feeds?${params.toString()}`);

            if (response.data.success) {
                setFeeds(response.data.data || []);
                setFeedsTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch price feeds');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch price feeds');
        } finally {
            setFeedsLoading(false);
        }
    }, [canView, feedsPage, feedsRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchFeeds();
    }, [fetchFeeds, canView, activeTab]);

    // Feed handlers
    const resetFeedForm = () => {
        setFeedFormData({ symbol_id: null, broker_api: '', connection_type: 'websocket', refresh_interval: 60, status: 'STREAMING' });
        setFeedFormErrors({});
    };

    const handleFeedCreateOpen = () => {
        resetFeedForm();
        setFeedEditMode(false);
        setSelectedFeed(null);
        setFeedDialogOpen(true);
    };

    const handleFeedEditOpen = (feed) => {
        setSelectedFeed(feed);
        setFeedEditMode(true);
        const sym = symbols.find(s => s.id === feed.symbol_id) || null;
        setFeedFormData({
            symbol_id: sym,
            broker_api: feed.broker_api || '',
            connection_type: feed.connection_type || 'websocket',
            refresh_interval_seconds: feed.refresh_interval_seconds || 60,
            status: feed.status || 'STREAMING',
        });
        setFeedFormErrors({});
        setFeedDialogOpen(true);
    };

    const handleFeedDialogClose = () => {
        setFeedDialogOpen(false);
        resetFeedForm();
        setSelectedFeed(null);
    };

    const handleFeedFormChange = (field) => (event) => {
        setFeedFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (feedFormErrors[field]) setFeedFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateFeedForm = () => {
        const errors = {};
        if (!feedFormData.symbol_id) errors.symbol_id = 'Symbol is required';
        if (!feedFormData.broker_api.trim()) errors.broker_api = 'Broker API is required';
        if (!feedFormData.connection_type) errors.connection_type = 'Connection type is required';
        setFeedFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleFeedSubmit = async () => {
        if (!validateFeedForm()) return;
        setFeedSubmitting(true);

        try {
            const payload = {
                symbol_id: feedFormData.symbol_id?.id || feedFormData.symbol_id,
                broker_api: feedFormData.broker_api,
                connection_type: feedFormData.connection_type,
                refresh_interval_seconds: parseInt(feedFormData.refresh_interval_seconds, 10),
                status: feedFormData.status,
            };

            let response;
            if (feedEditMode && selectedFeed) {
                response = await axiosInstance.put(`/api/market-data-ingestion/price-feeds/${selectedFeed.id}`, payload);
            } else {
                response = await axiosInstance.post('/api/market-data-ingestion/price-feeds', payload);
            }

            if (response.data.success) {
                handleFeedDialogClose();
                fetchFeeds();
            } else {
                setFeedFormErrors({ submit: response.data.message || 'Failed to save price feed' });
            }
        } catch (err) {
            setFeedFormErrors({ submit: err.response?.data?.message || 'Failed to save price feed' });
        } finally {
            setFeedSubmitting(false);
        }
    };

    const handleFeedDeleteClick = (feed) => { setFeedToDelete(feed); setFeedDeleteDialogOpen(true); };

    const handleFeedDeleteConfirm = async () => {
        if (!feedToDelete) return;
        try {
            const response = await axiosInstance.delete(`/api/market-data-ingestion/price-feeds/${feedToDelete.id}`);
            if (response.data.success) {
                fetchFeeds();
                setFeedDeleteDialogOpen(false);
                setFeedToDelete(null);
            } else {
                setError(response.data.message || 'Failed to delete price feed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete price feed');
        }
    };

    // ========== Tab 1: Realized Volatility ==========
    const fetchRvRecords = useCallback(async () => {
        if (!canView || !rvSymbol) return;
        setRvLoading(true);

        try {
            const params = new URLSearchParams({ page: rvPage + 1, per_page: rvRowsPerPage });
            const response = await axiosInstance.get(`/api/market-data-ingestion/price-feeds/realized-volatility/${rvSymbol.id}?${params.toString()}`);

            if (response.data.success) {
                setRvRecords(response.data.data || []);
                setRvTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch RV records');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch RV records');
        } finally {
            setRvLoading(false);
        }
    }, [canView, rvSymbol, rvPage, rvRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 1 && rvSymbol) fetchRvRecords();
    }, [fetchRvRecords, canView, activeTab, rvSymbol]);

    const handleRvCalculate = async () => {
        if (!rvSymbol || !rvEstimator) return;
        setRvCalculating(true);

        try {
            const payload = {
                estimator_type: rvEstimator,
                window_days: parseInt(rvWindowDays, 10),
                interval: rvInterval,
            };
            const response = await axiosInstance.post(`/api/market-data-ingestion/price-feeds/realized-volatility/${rvSymbol.id}`, payload);

            if (response.data.success) {
                fetchRvRecords();
            } else {
                setError(response.data.message || 'Failed to calculate realized volatility');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to calculate realized volatility');
        } finally {
            setRvCalculating(false);
        }
    };

    // ========== Tab 2: Implied Volatility ==========
    const fetchIvRecords = useCallback(async () => {
        if (!canView || !ivSymbol) return;
        setIvLoading(true);

        try {
            const params = new URLSearchParams({ page: ivPage + 1, per_page: ivRowsPerPage });
            const response = await axiosInstance.get(`/api/market-data-ingestion/price-feeds/implied-volatility/${ivSymbol.id}?${params.toString()}`);

            if (response.data.success) {
                setIvRecords(response.data.data || []);
                setIvTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch IV records');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch IV records');
        } finally {
            setIvLoading(false);
        }
    }, [canView, ivSymbol, ivPage, ivRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 2 && ivSymbol) fetchIvRecords();
    }, [fetchIvRecords, canView, activeTab, ivSymbol]);

    const handleIvFetch = async () => {
        if (!ivSymbol) return;
        setIvFetching(true);

        try {
            const response = await axiosInstance.post(`/api/market-data-ingestion/price-feeds/implied-volatility/${ivSymbol.id}`);

            if (response.data.success) {
                fetchIvRecords();
            } else {
                setError(response.data.message || 'Failed to fetch implied volatility');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch implied volatility');
        } finally {
            setIvFetching(false);
        }
    };

    // ========== Tab 3: Corporate Actions ==========
    const fetchCorpActions = useCallback(async () => {
        if (!canView) return;
        setCorpLoading(true);

        try {
            const params = new URLSearchParams({ page: corpPage + 1, per_page: corpRowsPerPage });
            if (corpFilterSymbol) params.append('symbol_id', corpFilterSymbol);
            if (corpFilterType) params.append('action_type', corpFilterType);

            const response = await axiosInstance.get(`/api/market-data-ingestion/corporate-actions?${params.toString()}`);

            if (response.data.success) {
                setCorpActions(response.data.data || []);
                setCorpTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch corporate actions');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch corporate actions');
        } finally {
            setCorpLoading(false);
        }
    }, [canView, corpPage, corpRowsPerPage, corpFilterSymbol, corpFilterType]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchCorpActions();
    }, [fetchCorpActions, canView, activeTab]);

    // Corp action handlers
    const resetCorpForm = () => {
        setCorpFormData({ symbol_id: null, action_type: '', ex_date: '', record_date: '', pay_date: '', amount: '', ratio: '', description: '', affects_options: false });
        setCorpFormErrors({});
    };

    const handleCorpCreateOpen = () => {
        resetCorpForm();
        setCorpEditMode(false);
        setSelectedCorpAction(null);
        setCorpDialogOpen(true);
    };

    const handleCorpEditOpen = (action) => {
        setSelectedCorpAction(action);
        setCorpEditMode(true);
        const sym = symbols.find(s => s.id === action.symbol_id) || null;
        setCorpFormData({
            symbol_id: sym,
            action_type: action.action_type || '',
            ex_date: action.ex_date || '',
            record_date: action.record_date || '',
            pay_date: action.pay_date || '',
            amount: action.amount || '',
            ratio: action.ratio || '',
            description: action.description || '',
            affects_options: action.affects_options || false,
        });
        setCorpFormErrors({});
        setCorpDialogOpen(true);
    };

    const handleCorpDialogClose = () => {
        setCorpDialogOpen(false);
        resetCorpForm();
        setSelectedCorpAction(null);
    };

    const handleCorpFormChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setCorpFormData(prev => ({ ...prev, [field]: value }));
        if (corpFormErrors[field]) setCorpFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateCorpForm = () => {
        const errors = {};
        if (!corpFormData.symbol_id) errors.symbol_id = 'Symbol is required';
        if (!corpFormData.action_type) errors.action_type = 'Action type is required';
        if (!corpFormData.ex_date) errors.ex_date = 'Ex-date is required';
        setCorpFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCorpSubmit = async () => {
        if (!validateCorpForm()) return;
        setCorpSubmitting(true);

        try {
            const payload = {
                symbol_id: corpFormData.symbol_id?.id || corpFormData.symbol_id,
                action_type: corpFormData.action_type,
                ex_date: corpFormData.ex_date,
                record_date: corpFormData.record_date || undefined,
                pay_date: corpFormData.pay_date || undefined,
                amount: corpFormData.amount ? parseFloat(corpFormData.amount) : undefined,
                ratio: corpFormData.ratio || undefined,
                description: corpFormData.description || undefined,
                affects_options: corpFormData.affects_options,
            };

            let response;
            if (corpEditMode && selectedCorpAction) {
                response = await axiosInstance.put(`/api/market-data-ingestion/corporate-actions/${selectedCorpAction.id}`, payload);
            } else {
                response = await axiosInstance.post('/api/market-data-ingestion/corporate-actions', payload);
            }

            if (response.data.success) {
                handleCorpDialogClose();
                fetchCorpActions();
            } else {
                setCorpFormErrors({ submit: response.data.message || 'Failed to save corporate action' });
            }
        } catch (err) {
            setCorpFormErrors({ submit: err.response?.data?.message || 'Failed to save corporate action' });
        } finally {
            setCorpSubmitting(false);
        }
    };

    const handleCorpDeleteClick = (action) => { setCorpToDelete(action); setCorpDeleteDialogOpen(true); };

    const handleCorpDeleteConfirm = async () => {
        if (!corpToDelete) return;
        try {
            const response = await axiosInstance.delete(`/api/market-data-ingestion/corporate-actions/${corpToDelete.id}`);
            if (response.data.success) {
                fetchCorpActions();
                setCorpDeleteDialogOpen(false);
                setCorpToDelete(null);
            } else {
                setError(response.data.message || 'Failed to delete corporate action');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete corporate action');
        }
    };

    // ========== Tab 4: Earnings Calendar ==========
    const fetchEarnings = useCallback(async () => {
        if (!canView) return;
        setEarningsLoading(true);

        try {
            const params = new URLSearchParams({ page: earningsPage + 1, per_page: earningsRowsPerPage });
            if (earningsFilterSymbol) params.append('symbol_id', earningsFilterSymbol);
            if (earningsUpcomingOnly) params.append('upcoming_only', 'true');

            const response = await axiosInstance.get(`/api/market-data-ingestion/earnings-calendar?${params.toString()}`);

            if (response.data.success) {
                setEarnings(response.data.data || []);
                setEarningsTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch earnings calendar');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch earnings calendar');
        } finally {
            setEarningsLoading(false);
        }
    }, [canView, earningsPage, earningsRowsPerPage, earningsFilterSymbol, earningsUpcomingOnly]);

    useEffect(() => {
        if (canView && activeTab === 4) fetchEarnings();
    }, [fetchEarnings, canView, activeTab]);

    // ========== Helpers ==========
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    // ========== Permission Guard ==========
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view stock price feeds.</Alert>
            </Box>
        );
    }

    // ========== Tab 0: Price Feed Configs Render ==========
    const renderPriceFeedConfigs = () => (
        <Box>
            {/* Actions Bar */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchFeeds}><RefreshIcon /></IconButton>
                </Tooltip>
                {canAdd && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleFeedCreateOpen}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        Add Price Feed
                    </Button>
                )}
            </Box>

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Broker API</TableCell>
                                <TableCell>Connection Type</TableCell>
                                <TableCell>Refresh Interval</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last Streamed</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feedsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : feeds.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <ShowChartIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No price feed configs found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                feeds.map((feed) => (
                                    <TableRow key={feed.id} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold">{feed.symbol || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={BROKER_API_LABELS[feed.broker_api] || feed.broker_api || '-'} size="small" sx={{ fontFamily, fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={feed.connection_type || '-'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {feed.refresh_interval_seconds ? `${feed.refresh_interval_seconds}s` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={PriceFeedStatusLabels[feed.status] || feed.status || 'Unknown'}
                                                size="small"
                                                color={PriceFeedStatusColors[feed.status] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDateTime(feed.last_streamed_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleFeedEditOpen(feed)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleFeedDeleteClick(feed)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={feedsTotalCount}
                    page={feedsPage}
                    onPageChange={(e, newPage) => setFeedsPage(newPage)}
                    rowsPerPage={feedsRowsPerPage}
                    onRowsPerPageChange={(e) => { setFeedsRowsPerPage(parseInt(e.target.value, 10)); setFeedsPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );

    // ========== Tab 1: Realized Volatility Render ==========
    const renderRealizedVolatility = () => (
        <Box>
            {/* Controls */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            options={symbols}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={rvSymbol}
                            onChange={(e, newValue) => { setRvSymbol(newValue); setRvPage(0); }}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Symbol" size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Estimator Type</InputLabel>
                            <Select
                                value={rvEstimator}
                                label="Estimator Type"
                                onChange={(e) => setRvEstimator(e.target.value)}
                            >
                                {Object.entries(RVEstimatorType).map(([key, value]) => (
                                    <MenuItem key={key} value={value}>{RVEstimatorTypeLabels[value]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Interval</InputLabel>
                            <Select
                                value={rvInterval}
                                label="Interval"
                                onChange={(e) => setRvInterval(e.target.value)}
                            >
                                <MenuItem value="DAILY">Daily</MenuItem>
                                <MenuItem value="FOUR_HOUR">4 Hours</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Window Days"
                            type="number"
                            value={rvWindowDays}
                            onChange={(e) => setRvWindowDays(e.target.value)}
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Button
                            variant="contained"
                            startIcon={rvCalculating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                            onClick={handleRvCalculate}
                            disabled={!rvSymbol || !rvEstimator || rvCalculating}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Calculate
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* RV Results Table */}
            {!rvSymbol ? (
                <Paper sx={{ py: 6, textAlign: 'center' }}>
                    <AssessmentIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>Select a Symbol</Typography>
                    <Typography color="text.secondary" variant="body2">
                        Choose a symbol to view and calculate realized volatility
                    </Typography>
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Estimator Type</TableCell>
                                    <TableCell>Window Days</TableCell>
                                    <TableCell align="right">RV Value</TableCell>
                                    <TableCell align="right">Annualized Value</TableCell>
                                    <TableCell>Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rvLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                    </TableRow>
                                ) : rvRecords.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <TrendingUpIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography color="text.secondary">No realized volatility records found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rvRecords.map((record, index) => (
                                        <TableRow key={record.id || index} hover>
                                            <TableCell>
                                                <Chip
                                                    label={RVEstimatorTypeLabels[record.estimator_type] || record.estimator_type}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{record.window_days || '-'}</TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="medium">
                                                    {record.value != null ? parseFloat(record.value).toFixed(6) : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="medium">
                                                    {record.annualized_value != null ? parseFloat(record.annualized_value).toFixed(4) : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(record.calculation_date || record.created_at)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={rvTotalCount}
                        page={rvPage}
                        onPageChange={(e, newPage) => setRvPage(newPage)}
                        rowsPerPage={rvRowsPerPage}
                        onRowsPerPageChange={(e) => { setRvRowsPerPage(parseInt(e.target.value, 10)); setRvPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </Paper>
            )}
        </Box>
    );

    // ========== Tab 2: Implied Volatility Render ==========
    const renderImpliedVolatility = () => (
        <Box>
            {/* Controls */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                        <Autocomplete
                            options={symbols}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={ivSymbol}
                            onChange={(e, newValue) => { setIvSymbol(newValue); setIvPage(0); }}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Symbol" size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            startIcon={ivFetching ? <CircularProgress size={18} color="inherit" /> : <CompareArrowsIcon />}
                            onClick={handleIvFetch}
                            disabled={!ivSymbol || ivFetching}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Fetch IV from API
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchIvRecords} disabled={!ivSymbol}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>

            {/* IV Results Table */}
            {!ivSymbol ? (
                <Paper sx={{ py: 6, textAlign: 'center' }}>
                    <CompareArrowsIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>Select a Symbol</Typography>
                    <Typography color="text.secondary" variant="body2">
                        Choose a symbol to view and fetch implied volatility data
                    </Typography>
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">IV</TableCell>
                                    <TableCell align="right">RV (Annualized)</TableCell>
                                    <TableCell align="right">Value (IV - RV)</TableCell>
                                    <TableCell>Source</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ivLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                    </TableRow>
                                ) : ivRecords.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <CompareArrowsIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography color="text.secondary">No implied volatility records found</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Click "Fetch IV from API" to pull data from the provider
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    ivRecords.map((record, index) => {
                                        const diff = record.iv_rv_difference;
                                        return (
                                            <TableRow key={record.id || index} hover>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(record.fetch_date || record.created_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography fontWeight="medium">
                                                        {record.iv_value != null ? (parseFloat(record.iv_value) * 100).toFixed(2) + '%' : '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography fontWeight="medium">
                                                        {record.rv_value != null ? (parseFloat(record.rv_value) * 100).toFixed(2) + '%' : '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        fontWeight="bold"
                                                        sx={{ color: diff != null ? (diff > 0 ? '#f59e0b' : '#10b981') : 'inherit' }}
                                                    >
                                                        {diff != null ? (parseFloat(diff) * 100).toFixed(2) + '%' : '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={IVSourceLabels[record.source] || record.source || 'API'} size="small" variant="outlined" />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={ivTotalCount}
                        page={ivPage}
                        onPageChange={(e, newPage) => setIvPage(newPage)}
                        rowsPerPage={ivRowsPerPage}
                        onRowsPerPageChange={(e) => { setIvRowsPerPage(parseInt(e.target.value, 10)); setIvPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </Paper>
            )}
        </Box>
    );

    // ========== Tab 3: Corporate Actions Render ==========
    const renderCorporateActions = () => (
        <Box>
            {/* Filters + Actions */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Symbol</InputLabel>
                            <Select
                                value={corpFilterSymbol}
                                label="Filter by Symbol"
                                onChange={(e) => { setCorpFilterSymbol(e.target.value); setCorpPage(0); }}
                            >
                                <MenuItem value="">All Symbols</MenuItem>
                                {symbols.map(sym => (
                                    <MenuItem key={sym.id} value={sym.id}>{sym.symbol}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Type</InputLabel>
                            <Select
                                value={corpFilterType}
                                label="Filter by Type"
                                onChange={(e) => { setCorpFilterType(e.target.value); setCorpPage(0); }}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {Object.entries(CorporateActionType).map(([key, value]) => (
                                    <MenuItem key={key} value={value}>{CorporateActionTypeLabels[value]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchCorpActions}><RefreshIcon /></IconButton>
                        </Tooltip>
                        {canAdd && (
                            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCorpCreateOpen}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                Add Action
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Action Type</TableCell>
                                <TableCell>Ex-Date</TableCell>
                                <TableCell>Record Date</TableCell>
                                <TableCell>Pay Date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Ratio</TableCell>
                                <TableCell>Affects Options</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {corpLoading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : corpActions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <EventIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No corporate actions found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                corpActions.map((action) => (
                                    <TableRow key={action.id} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold">{action.symbol || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={CorporateActionTypeLabels[action.action_type] || action.action_type}
                                                size="small"
                                                color={CorporateActionTypeColors[action.action_type] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatDate(action.ex_date)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{formatDate(action.record_date)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{formatDate(action.pay_date)}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="medium">
                                                {action.amount != null ? `$${parseFloat(action.amount).toFixed(2)}` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{action.ratio || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={action.affects_options ? 'Yes' : 'No'}
                                                size="small"
                                                color={action.affects_options ? 'warning' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleCorpEditOpen(action)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleCorpDeleteClick(action)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={corpTotalCount}
                    page={corpPage}
                    onPageChange={(e, newPage) => setCorpPage(newPage)}
                    rowsPerPage={corpRowsPerPage}
                    onRowsPerPageChange={(e) => { setCorpRowsPerPage(parseInt(e.target.value, 10)); setCorpPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );

    // ========== Tab 3: Earnings Calendar Render ==========
    const renderEarningsCalendar = () => (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Symbol</InputLabel>
                            <Select
                                value={earningsFilterSymbol}
                                label="Filter by Symbol"
                                onChange={(e) => { setEarningsFilterSymbol(e.target.value); setEarningsPage(0); }}
                            >
                                <MenuItem value="">All Symbols</MenuItem>
                                {symbols.map(sym => (
                                    <MenuItem key={sym.id} value={sym.id}>{sym.symbol}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button
                            variant={earningsUpcomingOnly ? 'contained' : 'outlined'}
                            onClick={() => { setEarningsUpcomingOnly(prev => !prev); setEarningsPage(0); }}
                            startIcon={<EventIcon />}
                            sx={earningsUpcomingOnly ? { bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } } : {}}
                        >
                            Upcoming Only
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchEarnings}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Earnings Date</TableCell>
                                <TableCell>Quarter</TableCell>
                                <TableCell>Year</TableCell>
                                <TableCell align="right">Est. EPS</TableCell>
                                <TableCell align="right">Actual EPS</TableCell>
                                <TableCell align="right">Surprise %</TableCell>
                                <TableCell>Options Overlap</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {earningsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : earnings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <EventIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No earnings data found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                earnings.map((earning, index) => (
                                    <TableRow key={earning.id || index} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold">{earning.symbol || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatDate(earning.earnings_date)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{earning.quarter || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{earning.year || '-'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="medium">
                                                {earning.est_eps != null ? `$${parseFloat(earning.est_eps).toFixed(2)}` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="medium">
                                                {earning.actual_eps != null ? `$${parseFloat(earning.actual_eps).toFixed(2)}` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                fontWeight="medium"
                                                sx={{
                                                    color: earning.surprise_pct != null
                                                        ? (parseFloat(earning.surprise_pct) >= 0 ? '#10b981' : '#ef4444')
                                                        : 'inherit'
                                                }}
                                            >
                                                {earning.surprise_pct != null ? `${parseFloat(earning.surprise_pct).toFixed(2)}%` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={earning.options_overlap ? 'Yes' : 'No'}
                                                size="small"
                                                color={earning.options_overlap ? 'warning' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={earningsTotalCount}
                    page={earningsPage}
                    onPageChange={(e, newPage) => setEarningsPage(newPage)}
                    rowsPerPage={earningsRowsPerPage}
                    onRowsPerPageChange={(e) => { setEarningsRowsPerPage(parseInt(e.target.value, 10)); setEarningsPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );

    // ========== Main Render ==========
    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ShowChartIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Stock Price Feeds
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Manage price feeds, volatility calculations, corporate actions, and earnings
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Price Feed Configs" icon={<ShowChartIcon />} iconPosition="start" />
                    <Tab label="Realized Volatility" icon={<AssessmentIcon />} iconPosition="start" />
                    <Tab label="Implied Volatility" icon={<CompareArrowsIcon />} iconPosition="start" />
                    <Tab label="Corporate Actions" icon={<EventIcon />} iconPosition="start" />
                    <Tab label="Earnings Calendar" icon={<TrendingUpIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderPriceFeedConfigs()}
            {activeTab === 1 && renderRealizedVolatility()}
            {activeTab === 2 && renderImpliedVolatility()}
            {activeTab === 3 && renderCorporateActions()}
            {activeTab === 4 && renderEarningsCalendar()}

            {/* ========== Price Feed Add/Edit Dialog ========== */}
            <Dialog open={feedDialogOpen} onClose={handleFeedDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>{feedEditMode ? 'Edit Price Feed' : 'Add Price Feed'}</DialogTitle>
                <DialogContent>
                    {feedFormErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{feedFormErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbols}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={feedFormData.symbol_id}
                                onChange={(e, newValue) => {
                                    setFeedFormData(prev => ({ ...prev, symbol_id: newValue }));
                                    if (feedFormErrors.symbol_id) setFeedFormErrors(prev => ({ ...prev, symbol_id: null }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Symbol"
                                        error={!!feedFormErrors.symbol_id}
                                        helperText={feedFormErrors.symbol_id}
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!feedFormErrors.broker_api} required>
                                <InputLabel>Broker API</InputLabel>
                                <Select
                                    value={feedFormData.broker_api}
                                    onChange={handleFeedFormChange('broker_api')}
                                    label="Broker API"
                                >
                                    <MenuItem value="yahoo_finance">Yahoo Finance</MenuItem>
                                    <MenuItem value="alpha_vantage">Alpha Vantage</MenuItem>
                                    <MenuItem value="polygon">Polygon.io</MenuItem>
                                    <MenuItem value="alpaca">Alpaca Markets</MenuItem>
                                </Select>
                                {feedFormErrors.broker_api && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                        {feedFormErrors.broker_api}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!feedFormErrors.connection_type} required>
                                <InputLabel>Connection Type</InputLabel>
                                <Select
                                    value={feedFormData.connection_type}
                                    label="Connection Type"
                                    onChange={handleFeedFormChange('connection_type')}
                                >
                                    <MenuItem value="websocket">WebSocket</MenuItem>
                                    <MenuItem value="rest">REST</MenuItem>
                                </Select>
                                {feedFormErrors.connection_type && <Typography variant="caption" color="error">{feedFormErrors.connection_type}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Refresh Interval (seconds)"
                                type="number"
                                value={feedFormData.refresh_interval_seconds}
                                onChange={handleFeedFormChange('refresh_interval_seconds')}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={feedFormData.status}
                                    label="Status"
                                    onChange={handleFeedFormChange('status')}
                                >
                                    {Object.entries(PriceFeedStatus).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{PriceFeedStatusLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFeedDialogClose} disabled={feedSubmitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleFeedSubmit} disabled={feedSubmitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        {feedSubmitting ? <CircularProgress size={24} /> : (feedEditMode ? 'Save Changes' : 'Add Feed')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Price Feed Delete Confirmation */}
            <Dialog open={feedDeleteDialogOpen} onClose={() => setFeedDeleteDialogOpen(false)}>
                <DialogTitle>Delete Price Feed</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the price feed for <strong>{feedToDelete?.symbol}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFeedDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleFeedDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* ========== Corporate Action Add/Edit Dialog ========== */}
            <Dialog open={corpDialogOpen} onClose={handleCorpDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>{corpEditMode ? 'Edit Corporate Action' : 'Add Corporate Action'}</DialogTitle>
                <DialogContent>
                    {corpFormErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{corpFormErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={symbols}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={corpFormData.symbol_id}
                                onChange={(e, newValue) => {
                                    setCorpFormData(prev => ({ ...prev, symbol_id: newValue }));
                                    if (corpFormErrors.symbol_id) setCorpFormErrors(prev => ({ ...prev, symbol_id: null }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Symbol"
                                        error={!!corpFormErrors.symbol_id}
                                        helperText={corpFormErrors.symbol_id}
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!corpFormErrors.action_type} required>
                                <InputLabel>Action Type</InputLabel>
                                <Select
                                    value={corpFormData.action_type}
                                    label="Action Type"
                                    onChange={handleCorpFormChange('action_type')}
                                >
                                    {Object.entries(CorporateActionType).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{CorporateActionTypeLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                                {corpFormErrors.action_type && <Typography variant="caption" color="error">{corpFormErrors.action_type}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Ex-Date"
                                type="date"
                                value={corpFormData.ex_date}
                                onChange={handleCorpFormChange('ex_date')}
                                InputLabelProps={{ shrink: true }}
                                error={!!corpFormErrors.ex_date}
                                helperText={corpFormErrors.ex_date}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Record Date"
                                type="date"
                                value={corpFormData.record_date}
                                onChange={handleCorpFormChange('record_date')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Pay Date"
                                type="date"
                                value={corpFormData.pay_date}
                                onChange={handleCorpFormChange('pay_date')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Amount"
                                type="number"
                                value={corpFormData.amount}
                                onChange={handleCorpFormChange('amount')}
                                inputProps={{ step: '0.01', min: '0' }}
                                placeholder="0.00"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Ratio"
                                value={corpFormData.ratio}
                                onChange={handleCorpFormChange('ratio')}
                                placeholder="e.g. 2:1"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Affects Options</InputLabel>
                                <Select
                                    value={corpFormData.affects_options ? 'true' : 'false'}
                                    label="Affects Options"
                                    onChange={(e) => setCorpFormData(prev => ({ ...prev, affects_options: e.target.value === 'true' }))}
                                >
                                    <MenuItem value="false">No</MenuItem>
                                    <MenuItem value="true">Yes</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={corpFormData.description}
                                onChange={handleCorpFormChange('description')}
                                multiline
                                rows={2}
                                placeholder="Optional description..."
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCorpDialogClose} disabled={corpSubmitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleCorpSubmit} disabled={corpSubmitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        {corpSubmitting ? <CircularProgress size={24} /> : (corpEditMode ? 'Save Changes' : 'Add Action')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Corporate Action Delete Confirmation */}
            <Dialog open={corpDeleteDialogOpen} onClose={() => setCorpDeleteDialogOpen(false)}>
                <DialogTitle>Delete Corporate Action</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this <strong>{CorporateActionTypeLabels[corpToDelete?.action_type] || corpToDelete?.action_type}</strong> action
                        for <strong>{corpToDelete?.symbol}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCorpDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleCorpDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StockPriceFeedPage;
